import React from 'react';
import { useLatestPrice } from '../hooks/useMarketData';
import { formatPrice, formatPercentage, formatVolume, formatTimestamp } from '../utils/formatters';

export const PriceMetrics: React.FC = () => {
  const { data, isLoading, error } = useLatestPrice();

  if (isLoading) {
    return (
      <div className="bg-white rounded-lg shadow-lg p-6">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded w-1/3 mb-4"></div>
          <div className="h-12 bg-gray-200 rounded w-1/2 mb-2"></div>
          <div className="h-6 bg-gray-200 rounded w-1/4"></div>
        </div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="bg-white rounded-lg shadow-lg p-6">
        <div className="text-red-600">Failed to load price data</div>
      </div>
    );
  }

  const isPositive = data.change >= 0;
  const changeColor = isPositive ? 'text-green-600' : 'text-red-600';
  const bgColor = isPositive ? 'bg-green-50' : 'bg-red-50';

  return (
    <div className="bg-white rounded-lg shadow-lg p-6">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-2xl font-bold text-gray-800">{data.symbol}</h2>
          <p className="text-sm text-gray-500">E-mini S&P 500 Futures</p>
        </div>
        <div className="text-right">
          <p className="text-xs text-gray-400">Last Updated</p>
          <p className="text-sm text-gray-600">{formatTimestamp(data.timestamp)}</p>
        </div>
      </div>

      <div className="mb-6">
        <div className="flex items-baseline gap-3">
          <span className="text-4xl font-bold text-gray-900">
            ${formatPrice(data.close)}
          </span>
          <span className={`text-xl font-semibold ${changeColor}`}>
            {formatPercentage(data.changePercent)}
          </span>
        </div>
        <div className={`inline-block px-3 py-1 rounded-full mt-2 ${bgColor}`}>
          <span className={`text-sm font-medium ${changeColor}`}>
            {isPositive ? '▲' : '▼'} ${Math.abs(data.change).toFixed(2)}
          </span>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div>
          <p className="text-xs text-gray-500 uppercase">Open</p>
          <p className="text-lg font-semibold text-gray-800">${formatPrice(data.open)}</p>
        </div>
        <div>
          <p className="text-xs text-gray-500 uppercase">Day High</p>
          <p className="text-lg font-semibold text-gray-800">${formatPrice(data.dayHigh)}</p>
        </div>
        <div>
          <p className="text-xs text-gray-500 uppercase">Day Low</p>
          <p className="text-lg font-semibold text-gray-800">${formatPrice(data.dayLow)}</p>
        </div>
        <div>
          <p className="text-xs text-gray-500 uppercase">Volume</p>
          <p className="text-lg font-semibold text-gray-800">{formatVolume(data.volume)}</p>
        </div>
      </div>

      <div className="mt-6 pt-6 border-t border-gray-200">
        <div className="grid grid-cols-2 gap-4">
          <div>
            <p className="text-xs text-gray-500 uppercase">Previous Close</p>
            <p className="text-sm font-medium text-gray-700">${formatPrice(data.previousClose)}</p>
          </div>
          <div>
            <p className="text-xs text-gray-500 uppercase">52-Week Range</p>
            <p className="text-sm font-medium text-gray-700">
              ${formatPrice(data.fiftyTwoWeekLow)} - ${formatPrice(data.fiftyTwoWeekHigh)}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};
